var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
var _slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;
    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);
        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();
function _objectWithoutProperties(obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
}

/* eslint-disable import/no-extraneous-dependencies */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useField, ErrorMessage } from 'formik';
import { InputGroup, InputGroupAddon, Button, FormGroup, Label, Input, FormFeedback, FormText } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { startCase, isEqual } from 'lodash';
import DatePicker from 'react-date-picker';
import TimePicker from 'react-time-picker';
import DateTimePicker from 'react-datetime-picker';
import { Rifm } from 'rifm';
import PhoneInput from 'react-phone-number-input/basic-input';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import RadioGroup from './RadioGroup';
import CheckboxGroup from './CheckboxGroup';
var customInputs = ['radio-group', 'select', 'checkbox', 'checkbox-group', 'date', 'time', 'date-time', 'tel', 'mask', 'password'];
var Field = function Field(_ref) {
  var label = _ref.label,
    type = _ref.type,
    onChange = _ref.onChange,
    onBlur = _ref.onBlur,
    clearable = _ref.clearable,
    creatable = _ref.creatable,
    groupClassName = _ref.groupClassName,
    inline = _ref.inline,
    loading = _ref.loading,
    multi = _ref.multi,
    props = _objectWithoutProperties(_ref, ['label', 'type', 'onChange', 'onBlur', 'clearable', 'creatable', 'groupClassName', 'inline', 'loading', 'multi']);
  var _useField = useField(_extends({}, props, {
      type: type
    })),
    _useField2 = _slicedToArray(_useField, 3),
    field = _useField2[0],
    meta = _useField2[1],
    helpers = _useField2[2];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    viewPassword = _useState2[0],
    setViewPassword = _useState2[1];
  var classes = classnames({
    'is-invalid': meta.error && meta.touched
  }, props.className);
  var fieldGroupClasses = classnames({
    'mb-0': type === 'hidden'
  }, groupClassName);
  var customBaseInputClasses = classnames(classes, {
    'form-control-lg': props.size === 'lg',
    'form-control-sm': props.size === 'sm'
  });
  var customInputClasses = classnames(customBaseInputClasses, 'form-control');
  var customSelectClasses = classnames(customBaseInputClasses, 'form-select');
  var baseInputProps = {
    id: props.name,
    noValidate: true,
    error: meta.error,
    touched: meta.touched,
    placeholder: props.placeholder || 'Enter ' + startCase(typeof label === 'string' ? label : props.name),
    disabled: props.disabled || loading
  };
  var customBaseInputProps = {
    onChange: helpers.setValue,
    onBlur: helpers.setTouched
  };
  var customInputProps = _extends({}, baseInputProps, customBaseInputProps, {
    className: customInputClasses,
    clearable: clearable
  });
  var getOptionValue = props.getOptionValue;
  var options = props.options;
  var fixOptions = props.fixOptions;
  var value = field.value;
  var newValue = field.value;
  if (type === 'select' && fixOptions) {
    if (options && options.length > 0 && !loading) {
      if (options.every(function (o) {
        return typeof o === 'string';
      })) {
        options = options.map(function (o) {
          return {
            value: o,
            label: startCase(o)
          };
        });
      }
      if (Array.isArray(value)) {
        if (value.every(function (v) {
          return typeof v === 'string';
        })) {
          newValue = options.filter(function (o) {
            return value.includes(getOptionValue ? getOptionValue(o) : o.value);
          });
        } else if (getOptionValue) {
          newValue = options.filter(function (o) {
            return value.some(function (v) {
              return getOptionValue(v) === getOptionValue(o);
            });
          });
        } else {
          newValue = options.filter(function (o) {
            return value.some(function (v) {
              return v.value === o.value;
            });
          });
        }
      } else if (typeof value === 'string') {
        newValue = options.find(function (o) {
          return (getOptionValue ? getOptionValue(o) : o.value) === value;
        });
        if (multi) newValue = [value];
      }
      if (!isEqual(value, newValue)) helpers.setValue(newValue);
    }
  }
  var customSelectProps = _extends({}, customBaseInputProps, {
    className: customSelectClasses,
    isDisabled: props.disabled || loading,
    isLoading: loading,
    isMulti: multi,
    isClearable: clearable,
    classNamePrefix: 'react-select',
    options: options,
    value: value
  });
  var SelectComponent =
  // eslint-disable-next-line no-nested-ternary
  creatable ? props.async ? AsyncCreatableSelect : CreatableSelect : props.async ? AsyncSelect : Select;
  return React.createElement(FormGroup, {
    className: fieldGroupClasses,
    inline: inline
  }, type !== 'hidden' && label && React.createElement(Label, {
    'for': field.name,
    className: 'text-primary'
  }, typeof label === 'string' ? label : label && startCase(props.name), props.required && React.createElement('span', {
    className: 'text-danger ml-1'
  }, '*'), loading && React.createElement(FontAwesomeIcon, {
    icon: 'spinner',
    spin: true,
    className: 'ml-1 text-muted'
  })), !customInputs.includes(type) && React.createElement(Input, _extends({}, field, props, baseInputProps, {
    type: type,
    className: classes
  })), type === 'date' && React.createElement(DatePicker, _extends({}, field, props, customInputProps, {
    format: props.format,
    calendarType: 'US',
    calendarIcon: React.createElement(FontAwesomeIcon, {
      icon: 'calendar-alt'
    }),
    clearIcon: React.createElement(FontAwesomeIcon, {
      icon: 'times'
    })
  })), type === 'time' && React.createElement(TimePicker, _extends({}, field, props, customInputProps, {
    format: props.format,
    clockIcon: React.createElement(FontAwesomeIcon, {
      icon: 'clock'
    }),
    clearIcon: React.createElement(FontAwesomeIcon, {
      icon: 'times'
    })
  })), type === 'date-time' && React.createElement(DateTimePicker, _extends({}, field, props, customInputProps, {
    format: props.format,
    calendarIcon: React.createElement(FontAwesomeIcon, {
      icon: 'calendar-alt'
    }),
    clockIcon: React.createElement(FontAwesomeIcon, {
      icon: 'clock'
    }),
    clearIcon: React.createElement(FontAwesomeIcon, {
      icon: 'times'
    })
  })), type === 'mask' && props.format && React.createElement(Rifm, _extends({}, field, props, {
    format: props.format
  }), function (newProps) {
    return React.createElement(Input, _extends({}, field, newProps, customInputProps, {
      type: type,
      id: props.name
    }));
  }), type === 'tel' && React.createElement(PhoneInput, _extends({}, field, props, customInputProps, {
    value: field.value || '',
    country: 'US'
  })), type === 'select' && React.createElement(SelectComponent, _extends({}, field, props, customSelectProps)), type === 'radio-group' && React.createElement(RadioGroup, _extends({}, field, props, {
    inline: inline
  }, customInputProps)), type === 'checkbox' && React.createElement('div', {
    className: customInputClasses
  }, React.createElement(FormGroup, {
    check: true,
    inline: inline
  }, React.createElement(Input, _extends({}, field, props, baseInputProps, {
    type: type,
    noValidate: true
  })), React.createElement(Label, {
    'for': props.name,
    check: true
  }, props.placeholder ? props.placeholder : typeof label === 'string' ? label : startCase(props.name)))), type === 'checkbox-group' && React.createElement(CheckboxGroup, _extends({}, field, {
    value: meta.value
  }, props, {
    inline: inline
  }, customInputProps)), type === 'password' && React.createElement(InputGroup, {
    size: props.size,
    className: customBaseInputClasses + ' p-0'
  }, React.createElement(Input, _extends({}, field, props, baseInputProps, {
    type: 'password',
    className: viewPassword ? 'd-none' : 'd-block'
  })), React.createElement(Input, _extends({}, field, props, {
    id: props.name + '-show',
    type: 'text',
    className: viewPassword ? 'd-block rounded-left' : 'd-none'
  })), React.createElement(InputGroupAddon, {
    addonType: 'append'
  }, React.createElement(Button, {
    color: 'light',
    className: 'border border-' + (meta.error ? 'danger' : 'quaternary'),
    type: 'button',
    onClick: function onClick() {
      return setViewPassword(!viewPassword);
    },
    tabIndex: -1
  }, React.createElement(FontAwesomeIcon, {
    icon: viewPassword ? 'eye-slash' : 'eye',
    fixedWidth: true
  })))), React.createElement(ErrorMessage, {
    name: props.name,
    component: FormFeedback
  }), props.text && React.createElement(FormText, null, props.text));
};
Field.propTypes = {
  label: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  groupClassName: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg']),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  multi: PropTypes.bool,
  clearable: PropTypes.bool,
  creatable: PropTypes.bool,
  async: PropTypes.bool,
  required: PropTypes.bool,
  inline: PropTypes.bool,
  format: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  text: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape()])),
  getOptionValue: PropTypes.func,
  fixOptions: PropTypes.bool
};
Field.defaultProps = {
  label: true,
  type: 'text',
  className: undefined,
  groupClassName: undefined,
  size: undefined,
  placeholder: undefined,
  disabled: false,
  loading: false,
  multi: false,
  clearable: true,
  creatable: false,
  async: false,
  required: false,
  inline: false,
  format: undefined,
  text: undefined,
  onChange: undefined,
  onBlur: undefined,
  options: undefined,
  getOptionValue: undefined,
  fixOptions: true
};
export default Field;